import { t } from '@lingui/core/macro';

import {
  SERVICE_TYPE_CHOICES,
  SERVICE_TYPE_PRICE_UNITS,
} from '@rover/constants/src/translations/service.constants';
import type { ConversationDetail } from '@rover/rsdk/src/apiClient/latest/model/conversationDetail';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

function getConversationOpk(url): string {
  if (!url) {
    return '';
  }

  const match = url.match(/conversations\/([a-zA-Z0-9]+)\/$/);

  if (!match) {
    return '';
  }

  return match[1] || '';
}

export function getStayConversationOpk(url): string {
  if (!url) {
    return '';
  }
  const match = url.match(/account\/stays\/([a-zA-Z0-9]+)(?:\/|\?|$)/);
  if (!match) {
    return '';
  }
  return match[1] || '';
}

/**
 *
 * @param conversation
 * @returns {string} Returns total number of units for a conversation
 * @example
 * getTotalUnits(conversation);
 * // => '12 visits'
 * // => '2 walks'
 */
export const getTotalUnits = (conversation: ConversationDetail): string => {
  let numUnits = 0;
  if (!conversation.timeRanges) {
    numUnits = conversation.dates.reduce((acc, date) => {
      if (date.flexibleTimes != null && date.flexibleTimes?.length > 0) {
        return (
          acc +
          (date.flexibleTimes != null && date.flexibleTimes.length > 0
            ? date.flexibleTimes.length
            : 1)
        );
      }
      return acc + (date.times != null && date.times.length > 0 ? date.times.length : 1);
    }, 0);
  } else {
    numUnits = conversation.dates.length;
  }

  return `${numUnits} ${SERVICE_TYPE_PRICE_UNITS()[conversation.serviceType](numUnits)}`;
};

/**
 * @param conversation
 * @returns {string | null} Returns the duration of a unit
 * @example
 * getUnitDuration(conversation);
 * // => '30 min'
 * // => '60 min'
 * // => null
 */
export const getUnitDuration = (conversation: ConversationDetail): string | null => {
  if (
    [SERVICE_TYPE_CHOICES.walking, SERVICE_TYPE_CHOICES.dropIn].includes(
      conversation.serviceType as ServiceTypeSlug
    )
  ) {
    const hasActiveLongAddOn = conversation.bookingAddOns.reduce(
      (acc, addOn) => (addOn.active && ['long-drop-in', 'long-walk'].includes(addOn.slug)) || acc,
      false
    );

    return hasActiveLongAddOn ? t`60 min` : t`30 min`;
  }

  return null;
};

/**
 * @param conversation
 * @returns {string} Returns the total number of pets in a conversation
 * @example
 * getTotalPets(conversation);
 * // => '1 pet'
 * // => '5 pets'
 */
export const getTotalPets = (conversation: ConversationDetail): string => {
  const totalPets = conversation.pets.length;
  return totalPets > 1 ? t`${totalPets} pets` : t`1 pet`;
};

export default getConversationOpk;
